import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MediaCarouselModal } from "@/components/ui/MediaCarouselModal";
import {
  Loader2, MapPin, X, Check, Info, ShieldCheck, Tag,
  ChevronDown, ChevronUp, FileText,
} from "lucide-react";
import type { RateHawkRate, RateHawkRoomGroup, RateHawkSearchResult } from "../types";
import { WorldotaImage, resolveWorldotaUrl } from "../WorldotaImage";
import { formatCurrency, mealToTag, analyzeRates, resolveTaxData, formatMetapolicyEntries, parsePolicyText, type RateInsight } from "./helpers";
import { useI18n } from "@/hooks/useI18n";

// ─── rg_ext decoders ─────────────────────────────────────────────────────────

const RG_VIEW = ["", "Sea view", "Mountain view", "City view", "Pool view", "Garden view", "Lake view", "River view"];
const RG_BALCONY = ["", "Balcony", "Terrace"];
const RG_BATHROOM = ["", "Shared bathroom", "Private bathroom"];

function decodeRgExt(rg: RateHawkRate["rg_ext"]): string[] {
  if (!rg) return [];
  const tags: string[] = [];
  if (rg.capacity > 0) tags.push(`Up to ${rg.capacity} guest${rg.capacity > 1 ? "s" : ""}`);
  if (rg.bedrooms > 0) tags.push(`${rg.bedrooms} bedroom${rg.bedrooms > 1 ? "s" : ""}`);
  if (rg.view > 0 && RG_VIEW[rg.view]) tags.push(RG_VIEW[rg.view]);
  if (rg.balcony > 0 && RG_BALCONY[rg.balcony]) tags.push(RG_BALCONY[rg.balcony]);
  if (rg.bathroom > 0 && RG_BATHROOM[rg.bathroom]) tags.push(RG_BATHROOM[rg.bathroom]);
  if (rg.family === 1) tags.push("Family-friendly");
  if (rg.club === 1) tags.push("Club room");
  if (rg.floor > 0) tags.push(`Floor ${rg.floor}`);
  return tags;
}

interface RatesViewProps {
  roomGroups: RateHawkRoomGroup[];
  hotel: RateHawkSearchResult | null;
  isLoading: boolean;
  error: string | null;
  onSelect: (rate: RateHawkRate, room: RateHawkRoomGroup) => void;
  showRoomsHint?: boolean;
}

export function RatesView({ roomGroups, hotel, isLoading, error, onSelect, showRoomsHint }: RatesViewProps) {
  const { t } = useI18n();
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselMedia, setCarouselMedia] = useState<string[]>([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const VISIBLE_COUNT = 2;
  const visibleGroups = showAll ? roomGroups : roomGroups.slice(0, VISIBLE_COUNT);
  const hiddenCount = roomGroups.length - VISIBLE_COUNT;

  const openCarousel = useCallback((photos: string[], index = 0) => {
    setCarouselMedia(photos);
    setCarouselIndex(index);
    setCarouselOpen(true);
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500 mb-4" />
        <p className="text-gray-500">Loading room rates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <X className="h-10 w-10 text-red-400 mb-4" />
        <p className="text-red-600 font-medium">Error loading rates</p>
        <p className="text-gray-500 mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-4 overflow-y-auto">
      <MediaCarouselModal
        isOpen={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        media={carouselMedia}
        initialIndex={carouselIndex}
      />

      {/* Property header */}
      {hotel && (
        <div className="flex gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          {hotel.coverPhoto && (
            <WorldotaImage
              url={hotel.coverPhoto}
              alt={hotel.name}
              className="w-24 h-24 rounded-lg flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() =>
                openCarousel(
                  (hotel.photos?.filter(Boolean) ?? [hotel.coverPhoto!]).map(resolveWorldotaUrl)
                )
              }
            />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-gray-900">{hotel.name}</h2>
            <p className="text-gray-500 flex items-center gap-1 mt-0.5">
              <MapPin className="h-3.5 w-3.5" />
              {[hotel.address, hotel.cityName].filter(Boolean).join(", ")}
            </p>
            {hotel.checkInTime && hotel.checkOutTime && (
              <p className="text-sm text-gray-400 mt-1">
                Check-in {hotel.checkInTime.slice(0, 5)} · Check-out {hotel.checkOutTime.slice(0, 5)}
              </p>
            )}
          </div>
          <HotelPoliciesBox hotel={hotel} />
        </div>
      )}

      {showRoomsHint && (
        <div className="flex items-start gap-2 mb-4 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
          <span>{t('builder.stays.multiroom_hint')}</span>
        </div>
      )}

      {roomGroups.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No rates available for this property.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
          {visibleGroups.map((group, idx) => (
            <RoomSection key={idx} group={group} onSelect={onSelect} onOpenCarousel={openCarousel} />
          ))}
        </div>
      )}

      {/* Show more / show less toggle */}
      {roomGroups.length > VISIBLE_COUNT && (
        <div className="flex justify-center mt-4 px-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAll((v) => !v)}
            className="gap-1.5 text-gray-600"
          >
            {showAll ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Show {hiddenCount} more option{hiddenCount > 1 ? "s" : ""}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Hotel Policies (metapolicy_struct / metapolicy_extra_info) ──────────────
// Always-visible text box, top-right of the hotel title row.

function HotelPoliciesBox({ hotel }: { hotel: RateHawkSearchResult }) {
  const { t } = useI18n();
  const entries = useMemo(() => formatMetapolicyEntries(hotel.metapolicy_struct), [hotel.metapolicy_struct]);
  const extraInfoLines = hotel.metapolicy_extra_info?.split("\n").filter(Boolean) ?? [];

  if (entries.length === 0 && extraInfoLines.length === 0) return null;

  return (
    <div className="flex-shrink-0 self-start w-1/2 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
      <p className="flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
        <FileText className="h-3 w-3" />
        {t('builder.stays.hotel_policies_title')}
      </p>
      <div className="flex flex-col gap-1">
        {entries.map(({ key, label, text }) => (
          <p key={key} className="text-xs text-gray-600">
            <span className="font-medium text-gray-700">{label}:</span> {text}
          </p>
        ))}
        {extraInfoLines.map((line, i) => (
          <p key={i} className="text-xs text-gray-600">
            {parsePolicyText(line).map((seg, j) =>
              seg.href ? (
                <a
                  key={j}
                  href={seg.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {seg.text}
                </a>
              ) : (
                <span key={j}>{seg.text}</span>
              ),
            )}
          </p>
        ))}
      </div>
    </div>
  );
}

// ─── Room Section ─────────────────────────────────────────────────────────────

function RoomSection({
  group,
  onSelect,
  onOpenCarousel,
}: {
  group: RateHawkRoomGroup;
  onSelect: (rate: RateHawkRate, room: RateHawkRoomGroup) => void;
  onOpenCarousel: (photos: string[], index?: number) => void;
}) {
  const photo = group.photos?.[0];
  const allPhotos = group.photos?.filter(Boolean) ?? [];
  const beds = group.bedsLabel;

  const insights = useMemo(() => analyzeRates(group.rates), [group.rates]);

  // Decode room attributes from the first rate's rg_ext
  const firstRate = group.rates[0];
  const rgAttrs = useMemo(() => decodeRgExt(firstRate?.rg_ext), [firstRate]);
  const beddingType = firstRate?.room_data_trans?.bedding_type;
  const bathroomType = firstRate?.room_data_trans?.bathroom;

  return (
    <div>
      {/* Room header */}
      <div className="flex items-center gap-3 mb-3">
        <WorldotaImage
          url={photo ?? ""}
          alt={group.roomName}
          className="w-16 h-16 rounded-lg flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => onOpenCarousel(allPhotos.map(resolveWorldotaUrl))}
        />
        <div className="min-w-0">
          <h4 className="text-gray-900">{group.roomName}</h4>
          {(beds || beddingType) && (
            <p className="text-sm text-gray-500">{beds || beddingType}</p>
          )}
          {bathroomType && (
            <p className="text-sm text-gray-400">{bathroomType}</p>
          )}
          {rgAttrs.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {rgAttrs.map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rates for this room group */}
      <div className="flex flex-col gap-2 mt-3">
        {group.rates.map((rate) => (
          <RateCard
            key={rate.match_hash}
            rate={rate}
            group={group}
            insight={insights.get(rate.match_hash)}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Rate Card ────────────────────────────────────────────────────────────────

function RateCard({
  rate,
  group,
  insight,
  onSelect,
}: {
  rate: RateHawkRate;
  group: RateHawkRoomGroup;
  insight?: RateInsight;
  onSelect: (r: RateHawkRate, room: RateHawkRoomGroup) => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const { t } = useI18n();

  const cancellationLabel = insight?.cancellationLabel ?? "non-refundable";
  const badge = insight?.badge ?? null;
  const pt = rate.payment_options?.payment_types?.[0];
  const showAmount = rate.show_amount ?? pt?.show_amount;
  const showCurrency = rate.show_currency_code ?? pt?.show_currency_code ?? "EUR";

  const noShowAmount = parseFloat(rate.no_show?.amount ?? "0");
  const hasNoShowPenalty = noShowAmount > 0;
  const hasDeposit = !!rate.deposit;
  const taxData = resolveTaxData(rate);
  const hasTaxData = (taxData?.taxes?.length ?? 0) > 0;
  const nonIncludedTaxes = taxData?.taxes?.filter(tax => !tax.included_by_supplier) ?? [];
  const includedTaxes = taxData?.taxes?.filter(tax => tax.included_by_supplier) ?? [];
  const hasNonIncludedTaxes = nonIncludedTaxes.length > 0;
  const hasIncludedTaxes = includedTaxes.length > 0;
  const hasRateAmenities = (rate.amenities_data?.length ?? 0) > 0;
  const hasDetails = hasRateAmenities || hasNoShowPenalty || hasDeposit || hasTaxData
    || rate.is_package || !rate.any_residency
    || (rate.daily_prices?.length ?? 0) > 1;

  const borderClass =
    badge === "best-price" || badge === "best-deal"
      ? "border-green-300 bg-green-50/30"
      : badge === "flexible"
        ? "border-blue-300 bg-blue-50/30"
        : "border-gray-200";

  return (
    <TooltipProvider delayDuration={200}>
      <div className={`border rounded-lg px-4 py-3 hover:shadow-sm transition-all ${borderClass}`}>
        <div className="flex items-center gap-4">
          {/* Details */}
          <div className="flex-1 min-w-0">
            {/* Meal plan + badges + allotment row */}
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-gray-900">{mealToTag(rate.meal)}</p>
              {badge === "best-price" && (
                <Badge variant="success" className="text-[10px] px-1.5 py-0">
                  <Tag className="h-2.5 w-2.5 mr-0.5" /> Best price
                </Badge>
              )}
              {badge === "best-deal" && (
                <Badge variant="success" className="text-[10px] px-1.5 py-0">
                  <Tag className="h-2.5 w-2.5 mr-0.5" /> Best deal
                </Badge>
              )}
              {badge === "flexible" && (
                <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
                  <ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Flexible
                </Badge>
              )}
              {rate.is_package && (
                <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
                  Package
                </Badge>
              )}
              {rate.allotment > 0 && rate.allotment <= 5 && (
                <span className="text-[11px] text-red-600 font-medium">
                  Only {rate.allotment} left
                </span>
              )}
            </div>

            {/* Cancellation + payment type */}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {cancellationLabel === "free" ? (
                <span className="text-sm text-green-600 flex items-center gap-0.5">
                  <Check className="h-3 w-3" /> Free cancellation
                  {insight?.cancellationDeadlineFormatted && (
                    <span className="text-gray-400 ml-0.5">
                      until {insight.cancellationDeadlineFormatted}
                    </span>
                  )}
                </span>
              ) : cancellationLabel === "partial" ? (
                <span className="text-sm text-amber-600 flex items-center gap-0.5">
                  <ShieldCheck className="h-3 w-3" /> Partially refundable
                  {insight?.refundFormatted && (
                    <span className="font-medium ml-0.5">({insight.refundFormatted})</span>
                  )}
                  {insight?.cancellationDeadlineFormatted && (
                    <span className="text-gray-400 ml-0.5">
                      until {insight.cancellationDeadlineFormatted}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-sm text-gray-500 flex items-center gap-0.5">
                  <X className="h-3 w-3" /> Non-refundable
                </span>
              )}
              {pt?.type && (
                <span className="text-sm text-gray-400">· {pt.type.replace(/_/g, " ")}</span>
              )}
            </div>
          </div>

          {/* Price & Action */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right">
              <h4 className="text-gray-900">
                {formatCurrency(showAmount, showCurrency)}
              </h4>
              {insight?.priceDiffFormatted && (
                <p className="text-sm text-gray-400">{insight.priceDiffFormatted}</p>
              )}
              {hasNonIncludedTaxes && (
                <div className="mt-1 flex flex-col gap-0.5">
                  {nonIncludedTaxes.map((tax, i) => (
                    <p key={i} className="text-[11px] text-amber-700 font-medium">
                      + {tax.name.replace(/_/g, " ")}: {formatCurrency(tax.amount, tax.currency_code)}
                    </p>
                  ))}
                  <p className="text-[10px] text-amber-600">{t('builder.stays.taxes_pay_at_checkin')}</p>
                </div>
              )}
            </div>

            {insight?.comparisonNote && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-sm">
                  {insight.comparisonNote}
                </TooltipContent>
              </Tooltip>
            )}

            <Button size="sm" onClick={() => onSelect(rate, group)}>
              Select
            </Button>
          </div>
        </div>

        {/* Expandable details panel */}
        {hasDetails && (
          <>
            <button
              onClick={() => setShowDetails((v) => !v)}
              className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
            >
              {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showDetails ? "Hide details" : "Show details"}
            </button>

            {showDetails && (
              <div className="mt-2 pt-2 border-t border-gray-100 flex flex-col gap-2">
                {/* Daily price breakdown */}
                {(rate.daily_prices?.length ?? 0) > 1 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Price per night</p>
                    <div className="flex flex-wrap gap-1">
                      {rate.daily_prices.map((p, i) => (
                        <span key={i} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                          Night {i + 1}: {formatCurrency(p, showCurrency)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rate-level amenities */}
                {hasRateAmenities && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Room amenities</p>
                    <div className="flex flex-wrap gap-1">
                      {rate.amenities_data!.map((a) => (
                        <span key={a} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                          {a.replace(/-/g, " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* No-show penalty */}
                {hasNoShowPenalty && (
                  <p className="text-xs text-amber-700">
                    No-show penalty: {formatCurrency(rate.no_show!.amount, rate.no_show!.currency_code)}
                    {rate.no_show!.from_time && rate.no_show!.from_time !== "00:00:00" && (
                      <span className="text-gray-400"> from {rate.no_show!.from_time}</span>
                    )}
                  </p>
                )}

                {/* Deposit */}
                {hasDeposit && (
                  <p className="text-xs text-gray-600">
                    Deposit: {formatCurrency(rate.deposit!.amount, rate.deposit!.currency_code)}
                    {" · "}
                    {rate.deposit!.is_refundable ? "refundable" : "non-refundable"}
                  </p>
                )}

                {/* Non-included taxes — anche mostrate sopra ma ripetiamo qui per chiarezza */}
                {hasNonIncludedTaxes && (
                  <div>
                    <p className="text-xs font-medium text-amber-700 mb-1">{t('builder.stays.taxes_not_included')}</p>
                    {nonIncludedTaxes.map((tax, i) => (
                      <p key={i} className="text-xs text-amber-700">
                        {tax.name.replace(/_/g, " ")}: {formatCurrency(tax.amount, tax.currency_code)}
                      </p>
                    ))}
                    <p className="text-[10px] text-amber-600 mt-0.5">{t('builder.stays.taxes_pay_at_checkin')}</p>
                  </div>
                )}
                {/* Included taxes — in details only */}
                {hasIncludedTaxes && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">{t('builder.stays.taxes_included')}</p>
                    {includedTaxes.map((tax, i) => (
                      <p key={i} className="text-xs text-gray-500">
                        {tax.name.replace(/_/g, " ")}: {formatCurrency(tax.amount, tax.currency_code)}
                      </p>
                    ))}
                  </div>
                )}

                {/* Restrictions */}
                {!rate.any_residency && (
                  <p className="text-xs text-amber-700">Residency restriction may apply</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
